import { ColumnsType } from 'antd/es/table';
import React, { FC, memo, useCallback, useMemo } from 'react';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { showNotification } from 'app/providers/NotificationsProvider';
import { downloadPdfBlob, Email, EmailType, useGenerateEmailPdfMutation, useGetSavedEmailsQuery } from 'entities/Email';
import { RenderHTMLModal, useRenderHTMLModal } from 'features/RenderHTML';
import { RenderPDFModal, useRenderPDFModal } from 'features/RenderPDF';
import { ReactComponent as EmailIcon } from 'shared/assets/icons/EmailIcon.svg';
import { ReactComponent as UploadIcon } from 'shared/assets/icons/UploadIcon.svg';
import { ReactComponent as DocumentIcon } from 'shared/assets/icons/DocumentIcon.svg';
import { Modal } from 'shared/ui/Modal';
import { ServerTable } from 'shared/ui/ServerTable';
import { ServerTableRowActions } from 'shared/ui/ServerTable/types';
import { getClientDateFormat } from 'shared/utils/helpers/getDateFormat';
import { useEmailHistoryModal } from '../utils/hooks/useEmailHistoryModal';

export const EmailHistoryModal: FC = memo(() => {
  const { t } = useAppTranslation(['user', 'common']);

  const { isOpened, email, closeEmailHistoryModal } = useEmailHistoryModal();
  const { openRenderPDFModal } = useRenderPDFModal();
  const { openRenderHTMLModal } = useRenderHTMLModal();
  const [generateEmailPdf] = useGenerateEmailPdfMutation();

  const handleDownloadEmailPdf = useCallback(
    async (selectedEmail: Email): Promise<void> => {
      try {
        const blob = await generateEmailPdf({ htmlTemplate: selectedEmail.htmlTemplate }).unwrap();

        downloadPdfBlob(blob, `${selectedEmail.emailType}_${selectedEmail.emailId}`);
      } catch (error) {
        showNotification('error', t('Error', { ns: 'common' }), t('Error when downloading data', { ns: 'common' }));
        console.error('Error generating email PDF', error);
      }
    },
    [generateEmailPdf, t],
  );

  const columns = useMemo<ColumnsType<Email>>(
    () => [
      {
        title: t('Email'),
        key: 'email',
        render: (_, record) => record.to,
      },
      {
        title: t('Type'),
        key: 'emailType',
        render: (_, record) => t(record.emailType),
      },

      { title: t('Sent'), key: 'sent', render: (_, record) => getClientDateFormat(record.createdAt) },
    ],
    [t],
  );

  const rowActions: Array<ServerTableRowActions<Email>> = useMemo(
    () => [
      {
        name: 'downloadEmailPdf',
        icon: <UploadIcon className="[&>path]:stroke-accent w-6 h-6" />,
        theme: 'clear',
        description: t('Download PDF'),
        onClick: handleDownloadEmailPdf,
        isDisabled: (selectedEmail) => !selectedEmail.htmlTemplate,
      },
      {
        name: 'showMail',
        icon: <EmailIcon className="[&>path]:stroke-accent" />,
        theme: 'clear',
        description: t('Mail'),
        onClick: (selectedEmail) => {
          openRenderHTMLModal(selectedEmail.htmlTemplate);
        },
      },
      {
        name: 'showPdfAttachment',
        icon: <DocumentIcon className="[&>path]:stroke-accent" />,
        theme: 'clear',
        description: t('PDF attachment'),
        onClick: (selectedEmail) => {
          selectedEmail.base64Attachment && openRenderPDFModal(selectedEmail.base64Attachment);
        },
        isDisabled: (selectedEmail) => !selectedEmail.base64Attachment,
      },
    ],
    [handleDownloadEmailPdf, openRenderHTMLModal, openRenderPDFModal, t],
  );

  const allowedEmailTypes = Object.values(EmailType)
    .filter(
      (emailType) => emailType !== EmailType.CREATE_ACCOUNT && emailType !== EmailType.LOGIN_LINK && emailType !== EmailType.RESET_PASSWORD,
    )
    .map((emailType) => emailType);

  return (
    <>
      <Modal width={1100} isOpen={isOpened} onClose={closeEmailHistoryModal}>
        <ServerTable
          columns={columns}
          fetchData={useGetSavedEmailsQuery}
          defaultFilters={{
            to: email,
            emailType: allowedEmailTypes,
          }}
          rowKey="emailId"
          title={t('Email history')}
          rowActions={rowActions}
        />
      </Modal>
      <RenderPDFModal />
      <RenderHTMLModal />
    </>
  );
});
